import axios from 'axios';

/**
 * Upload a single image file to Cloudflare R2 via backend API endpoint (/api/upload/image/).
 * @param {File|Blob|string} file - Image file to upload or existing URL
 * @param {string} folder - Destination subfolder (e.g. 'activity', 'programs', 'profile', 'blog')
 * @returns {Promise<string>} Public R2 URL
 */
export async function uploadImageToR2(file, folder = 'activity') {
  if (!file) return null;
  if (typeof file === 'string') return file;

  try {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('folder', folder);

    const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    const res = await axios.post(`${apiUrl}/api/upload/image/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return res.data.url;
  } catch (err) {
    console.error('Error uploading image to R2:', err);
    throw err;
  }
}

/**
 * Bulk upload multiple image files to Cloudflare R2 via backend API endpoint (/api/upload/image/).
 * Accepts an Array or FileList/Object of files, preserves any existing URLs, and uploads new files.
 * @param {Array|FileList|Object} images - Collection of image files or URLs
 * @param {string} folder - Destination subfolder ('activity', 'programs', 'badges', etc.)
 * @returns {Promise<Array<string>>} Array of public R2 URLs
 */
export async function bulkUploadImagesToR2(images, folder = 'activity') {
  if (!images) return [];

  const rawList = Array.isArray(images) ? images : Object.values(images);
  if (rawList.length === 0) return [];

  const existingUrls = [];
  const filesToUpload = [];

  for (const item of rawList) {
    if (typeof item === 'string') {
      existingUrls.push(item);
    } else if (item && typeof item === 'object') {
      filesToUpload.push(item);
    }
  }

  if (filesToUpload.length === 0) {
    return existingUrls;
  }

  try {
    const formData = new FormData();
    for (const file of filesToUpload) {
      formData.append('images', file);
    }
    formData.append('folder', folder);

    const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    const res = await axios.post(`${apiUrl}/api/upload/image/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    const newUrls = res.data.urls || (res.data.url ? [res.data.url] : []);
    return [...existingUrls, ...newUrls];
  } catch (err) {
    console.error('Error bulk uploading images to R2:', err);
    return existingUrls;
  }
}

// Backwards compatibility alias
export const bulkUploadImagesToCloudinary = bulkUploadImagesToR2;
export default bulkUploadImagesToR2;
